import { BaseFormComponent, DatasetFormData } from 'src/app/@shared/form/base-form.component';
import { Component, Input, inject } from '@angular/core';
import { FormControl } from '@angular/forms';
import { FormFieldModel } from 'src/app/@shared/form/form-field/form-field.model';
import { ModalController, ModalOptions } from '@ionic/angular';
import { Organization } from '@api/organization/@shared/organization.entity';
import { PasswordValidationService } from 'src/app/@core/password-validation.service';
import {
  PasswordValidator,
  PasswordValidatorErrors,
  PasswordValidatorOptions,
} from 'src/app/@shared/password.validator';
import { RoleListModalComponent } from 'src/app/role/role-list-modal/role-list-modal.component';
import { RoleModel } from 'src/app/role/@shared/role.model';
import { UserModel } from 'src/app/user/@shared/user.model';
import { UserService } from 'src/app/user/@shared/user.service';

type SelectModalId = 'roles';
type SelectModalItem = RoleModel;

const PASSWORD_FORM_KEY = 'password';

@Component({
  selector: 'user-form',
  standalone: false,
  styleUrls: ['./user-form.component.scss'],
  templateUrl: './user-form.component.html',
})
export class UserFormComponent extends BaseFormComponent<UserModel> {
  @Input() public isEditMode: boolean;

  public selectedItems: { [key in SelectModalId]: SelectModalItem[] } = {
    roles: [],
  };

  public isMultiSelect: { [key in SelectModalId]: boolean } = {
    roles: true,
  };

  public strongPasswordOptions: PasswordValidatorOptions;
  public passwordErrors: PasswordValidatorErrors = {};
  public readonly passwordFormKey: string = PASSWORD_FORM_KEY;

  private modalIsOpen: boolean;

  private userService = inject(UserService);
  private modalController = inject(ModalController);
  private passwordValidationService = inject(PasswordValidationService);

  public async openModal(result: { field: FormFieldModel; formGroupIndex?: number }): Promise<void> {
    if (this.modalIsOpen) {
      return Promise.resolve();
    }

    this.modalIsOpen = true;

    const { field, formGroupIndex } = result;

    let modal: HTMLIonModalElement | null;

    switch (field.id) {
      case 'roles':
        modal = await this.getRolesModal(this.datasetData.type, (this.selectedItems[field.id] as RoleModel[]).slice(), this.isMultiSelect[field.id]);
        break;

      default:
        console.error(`Case for ${field.id} not handled`);
    }

    if (!modal) {
      this.modalIsOpen = false;
      throw new Error('Modal has not been created');
    }

    await modal.present();

    const { data } = await modal.onWillDismiss();

    this.modalIsOpen = false;

    if (!data) {
      return Promise.resolve();
    }

    this.onModalValuesReceived(field, data, formGroupIndex);

    return Promise.resolve();
  }

  public removeItemFromSelectModal(id: SelectModalId, value: string): void {
    this.selectedItems[id] = this.selectedItems[id].filter((item) => {
      return item.displayName !== value;
    });

    this.editForm.patchValue({
      [id]: this.selectedItems[id].length
        ? this.selectedItems[id]
        : null,
    });

    this.editForm.markAsDirty();
  }

  protected onDataReceived(data: UserModel): void {
    this.datasetData = {
      ...data,
    } as UserModel;

    this.prepareForm();

    if (!this.isEditMode) {
      this.strongPasswordOptions = this.passwordValidationService.getDefaultOptions();

      this.editForm.addControl(PASSWORD_FORM_KEY, new FormControl('', PasswordValidator(this.strongPasswordOptions)));
      this.passwordErrors = this.editForm.get(PASSWORD_FORM_KEY).errors;

      this.editForm.valueChanges.subscribe(() => {
        const { errors } = this.editForm.get(PASSWORD_FORM_KEY);

        this.passwordErrors = errors || {};
      });

      return;
    }

    this.patchFormByDataset(this.datasetData);
  }

  protected onFormPatchedByDataset(): void {
    for (const key of Object.keys(this.selectedItems)) {
      const id = key as SelectModalId;

      if (this.datasetData[id]) {
        this.selectedItems[id] = Array.isArray(this.datasetData[id])
          ? this.datasetData[id]
          : [this.datasetData[id]];
      }
    }
  }

  protected async handleRequest(formData: DatasetFormData): Promise<boolean> {
    this.insertSelectedItems(formData);

    if (this.isEditMode) {
      return !!await this.userService.patch(this.datasetData.id, formData);
    }

    return !!await this.userService.createUser(formData, 'users');
  }

  private async getRolesModal(type: string, selectedItems: RoleModel[], isMultiSelect: boolean = false): Promise<HTMLIonModalElement> {
    const componentProps = {
      formType: type,
      isModal: true,
      isMultiSelect,
      selectedItems,
    };

    const options: ModalOptions = {
      component: RoleListModalComponent,
      componentProps,
      cssClass: 'proto-modal proto-modal--list',
      mode: 'md',
    };

    const topModal = await this.modalController.getTop();

    options.presentingElement = topModal || document.querySelector('ion-router-outlet');

    const modal = await this.modalController.create(options);

    options.componentProps.presentingElement = modal;

    return modal;
  }

  // editForm[key] only contains the formatted value
  private onModalValuesReceived(field: FormFieldModel, values: SelectModalItem[], formGroupIndex?: number): void {
    console.info('onModalValuesReceived', field, values, formGroupIndex);
    this.editForm.markAsDirty();

    this.selectedItems[field.id] = values;

    this.editForm.patchValue({
      [field.id]: values,
    });
  }

  private insertSelectedItems(data: Partial<Organization>): void {
    Object.keys(this.selectedItems)
      .forEach((key) => {
        const items = this.selectedItems[key];

        if (items.length) {
          if (this.isMultiSelect[key] === false) {
            data[key] = {
              id: items[0].id,
            };
          } else {
            data[key] = items.map((i) => {
              return {
                id: i.id,
              };
            });
          }
        } else {
          data[key] = this.isMultiSelect[key]
            ? []
            : null;
        }
      });
  }
}
